import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiWrite, list, type Role } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel } from '../components/ui'
import { thaiDateTime } from '../lib/format'

/**
 * Zero-trust network access and microsegmentation (part 102).
 *
 * The page shows the ordered policy set, the segments a policy can target, a live evaluator and
 * the append-only decision log. An evaluation is a real recorded decision: every press of
 * "ประเมินการเข้าถึง" writes one row in platform.zt_decisions, whether the answer is allow or
 * deny. The evaluator never displays a policy as "approximately matched"; it names the exact
 * rule that decided, or says that no rule matched and the default deny applied.
 */

type ZTEffect = 'allow' | 'deny'
type ZTProtocol = 'any' | 'tcp' | 'udp'

interface PortRange {
  from: number
  to: number
}

interface ZTSegment {
  id: string
  name: string
  description: string
  cidrs: string[]
  created_at: string
  updated_at: string
}

interface ZTPolicy {
  id: string
  name: string
  description: string
  effect: ZTEffect
  priority: number
  roles: string[]
  departments: string[]
  device_postures: string[]
  segment_ids: string[]
  protocol: ZTProtocol
  ports: PortRange[]
  source_cidrs: string[]
  days_of_week: number[]
  window_start_minute: number
  window_end_minute: number
  enabled: boolean
  created_at: string
  updated_at: string
}

interface ZTDecision {
  allowed: boolean
  effect: ZTEffect
  reason_code: string
  reason: string
  matched_policy_id?: string
  matched_policy_name?: string
  matched_priority?: number
  segment_name?: string
  decision_id?: string
  evaluated_at: string
}

interface ZTEvaluation extends ZTDecision {
  role: string
  department: string
  device_posture: string
  source_ip: string
  segment_id: string
  protocol: ZTProtocol
  port: number
  destination_ip?: string
}

interface ZTDecisionRow {
  id: string
  occurred_at: string
  actor_role: string
  subject_role: string
  department: string
  device_posture: string
  source_ip: string
  segment_id?: string
  segment_name: string
  destination_ip?: string
  protocol: string
  port: number
  allowed: boolean
  effect: ZTEffect
  reason_code: string
  reason: string
  matched_policy_id?: string
  matched_policy_name: string
  matched_priority?: number
  request_id: string
}

const roleOptions: Array<{ value: Role; label: string }> = [
  { value: 'owner', label: 'เจ้าของ' },
  { value: 'admin', label: 'ผู้ดูแลระบบ' },
  { value: 'audit_manager', label: 'ผู้จัดการงานตรวจสอบ' },
  { value: 'auditor', label: 'ผู้ตรวจสอบ' },
  { value: 'reviewer', label: 'ผู้ทบทวน' },
  { value: 'client_viewer', label: 'ผู้ดูฝั่งลูกค้า' },
]

const postureOptions: Array<{ value: string; label: string }> = [
  { value: 'managed', label: 'เครื่องที่บริษัทดูแล' },
  { value: 'compliant', label: 'ผ่านการตรวจสภาพ' },
  { value: 'unmanaged', label: 'เครื่องส่วนตัว' },
  { value: 'unknown', label: 'ไม่ทราบสถานะ' },
]

const protocolOptions: Array<{ value: ZTProtocol; label: string }> = [
  { value: 'any', label: 'ทุกโปรโตคอล' },
  { value: 'tcp', label: 'TCP' },
  { value: 'udp', label: 'UDP' },
]

const dayLabels = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.']
const dayNames = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']

const reasonLabels: Record<string, string> = {
  allowed: 'อนุญาตตามกฎ',
  denied_by_rule: 'ปฏิเสธตามกฎ',
  no_policy: 'ไม่มีนโยบายครอบคลุมเซกเมนต์',
  no_matching_policy: 'ไม่มีกฎที่ตรงกับคำขอ',
  unknown_segment: 'ไม่พบเซกเมนต์',
  destination_outside_segment: 'ปลายทางอยู่นอกเซกเมนต์',
  invalid_request: 'คำขอไม่ถูกต้อง',
}

const POLICY_KEY = ['zt-policies']
const SEGMENT_KEY = ['zt-segments']
const DECISION_KEY = ['zt-decisions']

function toggle<T>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value]
}

function toMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}

function fromMinutes(value: number): string {
  if (value >= 1440) return '24:00'
  const h = Math.floor(value / 60)
  const m = value % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function windowText(start: number, end: number): string {
  if (start === end) return 'ตลอดวัน (Asia/Bangkok)'
  return `${fromMinutes(start)}-${fromMinutes(end)} น. (Asia/Bangkok)`
}

function dayText(days: number[]): string {
  if (days.length === 0) return 'ทุกวัน'
  return days.map((d) => dayNames[d] ?? String(d)).join(', ')
}

function portText(ports: PortRange[]): string {
  if (ports.length === 0) return 'ทุกพอร์ต'
  return ports.map((p) => (p.from === p.to ? String(p.from) : `${p.from}-${p.to}`)).join(', ')
}

/** Returns null when any entry is malformed, so the form can refuse before calling the API. */
function parsePorts(raw: string): PortRange[] | null {
  const parts = raw.split(/[\n,]/).map((part) => part.trim()).filter(Boolean)
  const out: PortRange[] = []
  for (const part of parts) {
    const match = part.match(/^(\d{1,5})(?:\s*-\s*(\d{1,5}))?$/)
    if (!match) return null
    const from = Number(match[1])
    const to = match[2] ? Number(match[2]) : from
    if (from < 1 || to > 65535 || from > to) return null
    out.push({ from, to })
  }
  return out
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export function ZeroTrust() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const myRole: Role = me?.role ?? 'auditor'
  const canView = myRole === 'owner' || myRole === 'admin' || myRole === 'audit_manager' || myRole === 'auditor'
  const canConfigure = myRole === 'owner' || myRole === 'admin'

  const [notice, setNotice] = useState('')
  const [formError, setFormError] = useState('')

  // Policy form
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [effect, setEffect] = useState<ZTEffect>('allow')
  const [priority, setPriority] = useState('100')
  const [selectedRoles, setSelectedRoles] = useState<Role[]>(['auditor'])
  const [departments, setDepartments] = useState('')
  const [selectedPostures, setSelectedPostures] = useState<string[]>(['managed'])
  const [selectedSegments, setSelectedSegments] = useState<string[]>([])
  const [protocol, setProtocol] = useState<ZTProtocol>('tcp')
  const [ports, setPorts] = useState('443')
  const [cidrs, setCidrs] = useState('')
  const [days, setDays] = useState<number[]>([])
  const [windowStart, setWindowStart] = useState('08:00')
  const [windowEnd, setWindowEnd] = useState('17:30')
  const [enabled, setEnabled] = useState(true)

  // Evaluation form
  const [evalRole, setEvalRole] = useState<Role>(myRole)
  const [evalDepartment, setEvalDepartment] = useState('')
  const [evalPosture, setEvalPosture] = useState('managed')
  const [evalSource, setEvalSource] = useState('')
  const [evalSegment, setEvalSegment] = useState('')
  const [evalProtocol, setEvalProtocol] = useState<ZTProtocol>('tcp')
  const [evalPort, setEvalPort] = useState('443')
  const [evalDestination, setEvalDestination] = useState('')
  const [decision, setDecision] = useState<ZTEvaluation | null>(null)

  const [logFilter, setLogFilter] = useState<'all' | 'allowed' | 'denied'>('all')

  const policies = useQuery({
    queryKey: POLICY_KEY,
    queryFn: () => list<ZTPolicy>('/v1/zero-trust/policies'),
    enabled: canView,
    staleTime: 15_000,
  })

  const segments = useQuery({
    queryKey: SEGMENT_KEY,
    queryFn: () => list<ZTSegment>('/v1/zero-trust/segments'),
    enabled: canView,
    staleTime: 60_000,
  })

  const decisionLog = useQuery({
    queryKey: [...DECISION_KEY, logFilter],
    queryFn: () => list<ZTDecisionRow>(
      `/v1/zero-trust/decisions?limit=100${logFilter === 'all' ? '' : `&allowed=${logFilter === 'allowed'}`}`,
    ),
    enabled: canView,
    staleTime: 10_000,
  })

  const create = useMutation({
    mutationFn: (portRanges: PortRange[]) => apiWrite<ZTPolicy>('/v1/zero-trust/policies', 'POST', {
      name: name.trim(),
      description: description.trim(),
      effect,
      priority: Number.parseInt(priority, 10),
      roles: selectedRoles,
      departments: departments.split(/[\n,]/).map((d) => d.trim()).filter(Boolean),
      device_postures: selectedPostures,
      segment_ids: selectedSegments,
      protocol,
      ports: portRanges,
      source_cidrs: cidrs.split(/[\n,]/).map((c) => c.trim()).filter(Boolean),
      days_of_week: days,
      window_start_minute: toMinutes(windowStart),
      window_end_minute: toMinutes(windowEnd),
      enabled,
    }),
    onSuccess: (row) => {
      setNotice(`สร้างนโยบาย “${row.name}” แล้ว`)
      setFormError('')
      setName('')
      setDescription('')
      setCidrs('')
      setDays([])
      void qc.invalidateQueries({ queryKey: POLICY_KEY })
    },
    onError: (err) => {
      setNotice('')
      setFormError(errorText(err, 'บันทึกนโยบายไม่สำเร็จ'))
    },
  })

  const evaluate = useMutation({
    mutationFn: () => apiWrite<ZTEvaluation>('/v1/zero-trust/evaluate', 'POST', {
      role: evalRole,
      department: evalDepartment.trim(),
      device_posture: evalPosture,
      source_ip: evalSource.trim(),
      segment_id: chosenSegment,
      protocol: evalProtocol,
      port: Number.parseInt(evalPort, 10) || 0,
      destination_ip: evalDestination.trim(),
    }),
    onMutate: () => setDecision(null),
    onSuccess: (result) => {
      setDecision(result)
      void qc.invalidateQueries({ queryKey: DECISION_KEY })
    },
    onError: () => setDecision(null),
  })

  const policyRows = policies.data ?? []
  const segmentRows = segments.data ?? []
  const logRows = decisionLog.data ?? []
  const enabledCount = policyRows.filter((row) => row.enabled).length
  const denyCount = policyRows.filter((row) => row.effect === 'deny').length
  const deniedDecisions = logRows.filter((row) => !row.allowed).length
  const chosenSegment = evalSegment || segmentRows[0]?.id || ''
  const canSubmit = canConfigure
    && name.trim().length > 0
    && selectedSegments.length > 0
    && cidrs.trim().length > 0
    && !create.isPending

  function segmentName(id: string): string {
    return segmentRows.find((segment) => segment.id === id)?.name ?? id.slice(0, 8)
  }

  function submitPolicy() {
    setFormError('')
    setNotice('')
    const parsed = parsePorts(ports)
    if (parsed === null) {
      setFormError('รูปแบบพอร์ตไม่ถูกต้อง ใช้ 443 หรือ 8000-8080 คั่นด้วยจุลภาค')
      return
    }
    if (name.trim().length === 0) {
      setFormError('ต้องระบุชื่อนโยบาย')
      return
    }
    if (selectedSegments.length === 0) {
      setFormError('ต้องเลือกเซกเมนต์อย่างน้อยหนึ่งเซกเมนต์')
      return
    }
    if (cidrs.trim().length === 0) {
      setFormError('ต้องระบุ CIDR ต้นทางอย่างน้อยหนึ่งรายการ')
      return
    }
    const priorityValue = Number.parseInt(priority, 10)
    if (!Number.isInteger(priorityValue) || priorityValue < 0 || priorityValue > 1000) {
      setFormError('ลำดับความสำคัญต้องเป็นจำนวนเต็มระหว่าง 0 ถึง 1000')
      return
    }
    create.mutate(parsed)
  }

  function submitEvaluation() {
    const port = Number.parseInt(evalPort, 10)
    if (!Number.isInteger(port) || port < 1 || port > 65535) {
      setDecision(null)
      return
    }
    if (chosenSegment === '') {
      setDecision(null)
      return
    }
    evaluate.mutate()
  }

  if (!canView) {
    return (
      <>
        <PageHead title="การเข้าถึงเครือข่ายแบบ Zero Trust" sub="นโยบายไมโครเซกเมนต์และการตัดสินใจที่บันทึกไว้" />
        <div className="card">
          <Empty title="ไม่มีสิทธิ์เข้าถึง" body="หน้านี้เปิดให้เจ้าของ ผู้ดูแลระบบ ผู้จัดการงานตรวจสอบ และผู้ตรวจสอบเท่านั้น" />
        </div>
      </>
    )
  }

  return (
    <>
      <PageHead
        title="การเข้าถึงเครือข่ายแบบ Zero Trust"
        sub="กำหนดว่าใคร จากอุปกรณ์แบบใด และจากเครือข่ายใด เข้าถึงเซกเมนต์ใดได้ในพอร์ตและช่วงเวลาใด · ค่าเริ่มต้นคือปฏิเสธเมื่อไม่มีกฎที่ตรง"
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{policyRows.length} นโยบายทั้งหมด</span>
        <span className="chip">{enabledCount} นโยบายที่เปิดใช้</span>
        <span className="chip">{denyCount} กฎปฏิเสธ</span>
        <span className="chip">{segmentRows.length} เซกเมนต์</span>
        <span className="chip">{logRows.length} การตัดสินใจในบันทึก</span>
        <span className="chip">{deniedDecisions} ถูกปฏิเสธ</span>
      </div>

      <div className="grid-main">
        <div className="stack">
          <div className="card card-pad">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="card-label" style={{ marginBottom: 0 }}>นโยบายไมโครเซกเมนต์</div>
              <span className="dim" style={{ fontSize: 12 }}>เรียงตามลำดับที่ประเมินจริง</span>
            </div>

            {policies.isLoading ? (
              <div className="stack"><Skel h={54} /><Skel h={54} /><Skel h={54} /></div>
            ) : policyRows.length === 0 ? (
              <Empty
                title="ยังไม่มีนโยบาย"
                body="เมื่อไม่มีนโยบายที่เปิดใช้ ระบบจะปฏิเสธทุกคำขอเข้าถึงเซกเมนต์ สร้างกฎแรกเพื่ออนุญาตเฉพาะบทบาท แผนก อุปกรณ์ เครือข่าย พอร์ต และช่วงเวลาที่ต้องการ"
              />
            ) : (
              <div className="stack" style={{ gap: 12 }}>
                {policyRows.map((row) => (
                  <div key={row.id} style={{ border: '1px solid var(--color-hairline)', borderRadius: 14, padding: 14 }}>
                    <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div>
                        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                          <strong>{row.name}</strong>
                          <span className="chip" style={{
                            color: row.effect === 'deny' ? 'var(--color-amber)' : 'var(--color-emerald-soft)',
                            borderColor: row.effect === 'deny' ? 'rgba(199,123,43,0.32)' : 'rgba(46,125,100,0.32)',
                          }}>
                            {row.effect === 'deny' ? 'ปฏิเสธ' : 'อนุญาต'}
                          </span>
                          <span className="chip">ลำดับ {row.priority}</span>
                          <span className="chip">{row.enabled ? 'เปิดใช้' : 'ปิดใช้'}</span>
                        </div>
                        {row.description ? (
                          <div className="dim" style={{ marginTop: 6, fontSize: 12.5 }}>{row.description}</div>
                        ) : null}
                      </div>
                      <span className="mono muted" title={row.id}>{row.id.slice(0, 8)}</span>
                    </div>

                    <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                      {row.segment_ids.map((id) => (
                        <span className="chip gold" key={`seg-${id}`}>{segmentName(id)}</span>
                      ))}
                      {row.roles.length === 0 ? <span className="chip">ทุกบทบาท</span> : null}
                      {row.roles.map((role) => (
                        <span className="chip" key={`r-${role}`}>
                          {roleOptions.find((o) => o.value === role)?.label ?? role}
                        </span>
                      ))}
                      {row.departments.map((department) => (
                        <span className="chip" key={`d-${department}`}>{department}</span>
                      ))}
                      {row.device_postures.map((posture) => (
                        <span className="chip" key={`p-${posture}`}>
                          {postureOptions.find((o) => o.value === posture)?.label ?? posture}
                        </span>
                      ))}
                    </div>

                    <div className="row muted" style={{ gap: 12, flexWrap: 'wrap', marginTop: 10, fontSize: 12 }}>
                      <span className="mono">{row.protocol.toUpperCase()} · {portText(row.ports)}</span>
                      <span className="mono">{row.source_cidrs.join(', ')}</span>
                      <span>{dayText(row.days_of_week)}</span>
                      <span>{windowText(row.window_start_minute, row.window_end_minute)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card card-pad">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="card-label" style={{ marginBottom: 0 }}>บันทึกการตัดสินใจ</div>
              <select aria-label="กรองตามผลการตัดสิน"
                className="select"
                value={logFilter}
                onChange={(e) => setLogFilter(e.target.value as 'all' | 'allowed' | 'denied')}
                style={{ width: 150 }}
              >
                <option value="all">ทุกผลลัพธ์</option>
                <option value="allowed">อนุญาต</option>
                <option value="denied">ปฏิเสธ</option>
              </select>
            </div>

            {decisionLog.isLoading ? (
              <div className="stack"><Skel h={40} /><Skel h={40} /><Skel h={40} /></div>
            ) : logRows.length === 0 ? (
              <Empty
                title="ยังไม่มีบันทึก"
                body="ทุกครั้งที่ประเมินการเข้าถึง ระบบจะบันทึกผล กฎที่ใช้ตัดสิน และเหตุผลไว้ที่นี่ ทั้งกรณีอนุญาตและปฏิเสธ และบันทึกนี้แก้ไขไม่ได้"
              />
            ) : (
              <div className="table-wrap" style={{ border: 0 }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th>เวลา</th>
                      <th>ผู้ประเมิน</th>
                      <th>ตัวตนที่ประเมิน</th>
                      <th>เซกเมนต์และพอร์ต</th>
                      <th>ผล</th>
                      <th>เหตุผล</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logRows.map((row) => (
                      <tr key={row.id} style={{ cursor: 'default' }}>
                        <td className="mono" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>{thaiDateTime(row.occurred_at)}</td>
                        <td style={{ fontSize: 12 }}>
                          {roleOptions.find((o) => o.value === row.actor_role)?.label ?? row.actor_role}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          <div>{roleOptions.find((o) => o.value === row.subject_role)?.label ?? row.subject_role}</div>
                          <div className="muted" style={{ fontSize: 11 }}>
                            {row.department || 'ไม่ระบุแผนก'} · {postureOptions.find((o) => o.value === row.device_posture)?.label ?? row.device_posture}
                          </div>
                          <div className="mono muted" style={{ fontSize: 11 }}>{row.source_ip}</div>
                        </td>
                        <td style={{ fontSize: 12 }}>
                          <div>{row.segment_name || 'ไม่พบเซกเมนต์'}</div>
                          <div className="mono muted" style={{ fontSize: 11 }}>
                            {row.protocol.toUpperCase()} · {row.port}
                            {row.destination_ip ? ` · ${row.destination_ip}` : ''}
                          </div>
                        </td>
                        <td>
                          <span className="chip" style={{
                            color: row.allowed ? 'var(--color-emerald-soft)' : 'var(--color-amber)',
                            borderColor: row.allowed ? 'rgba(46,125,100,0.32)' : 'rgba(199,123,43,0.32)',
                          }}>
                            {row.allowed ? 'อนุญาต' : 'ปฏิเสธ'}
                          </span>
                        </td>
                        <td style={{ fontSize: 12, maxWidth: 380 }}>
                          <div>{row.reason}</div>
                          <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
                            {reasonLabels[row.reason_code] ?? row.reason_code}
                            {row.matched_policy_name ? ` · ${row.matched_policy_name}` : ''}
                            {row.matched_priority !== undefined ? ` · ลำดับ ${row.matched_priority}` : ''}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="stack">
          <div className="card card-pad">
            <div className="card-label">สร้างนโยบาย</div>
            <div className="dim" style={{ fontSize: 12, lineHeight: 1.7, marginTop: 8 }}>
              ลำดับเลขน้อยกว่าจะถูกประเมินก่อน และเมื่อลำดับเท่ากันกฎปฏิเสธจะชนะกฎอนุญาตเสมอ
            </div>

            <div className="field">
              <label htmlFor="zt-name">ชื่อนโยบาย</label>
              <input id="zt-name" className="input" value={name} maxLength={120}
                placeholder="เช่น ผู้ตรวจสอบเข้าฐานข้อมูลลูกค้าผ่าน HTTPS"
                onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="field">
              <label htmlFor="zt-description">คำอธิบาย</label>
              <textarea id="zt-description" className="input" rows={2} value={description}
                maxLength={500} placeholder="เหตุผลและขอบเขตของกฎ"
                onChange={(e) => setDescription(e.target.value)} />
            </div>

            <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <div className="field" style={{ flex: 1, minWidth: 150 }}>
                <label htmlFor="zt-effect">ผลของกฎ</label>
                <select id="zt-effect" className="select" value={effect}
                  onChange={(e) => setEffect(e.target.value as ZTEffect)}>
                  <option value="allow">อนุญาต</option>
                  <option value="deny">ปฏิเสธ</option>
                </select>
              </div>
              <div className="field" style={{ width: 120 }}>
                <label htmlFor="zt-priority">ลำดับ</label>
                <input id="zt-priority" className="input" inputMode="numeric" value={priority}
                  onChange={(e) => setPriority(e.target.value.replace(/[^0-9]/g, ''))} />
              </div>
            </div>

            <div className="field">
              <label>เซกเมนต์ปลายทาง</label>
              {segmentRows.length === 0 ? (
                <div className="form-error">
                  ยังไม่มีเซกเมนต์ในระบบ ต้องมีเซกเมนต์ก่อนจึงจะสร้างนโยบายได้
                </div>
              ) : (
                <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {segmentRows.map((segment) => (
                    <label
                      key={segment.id}
                      className={`chip${selectedSegments.includes(segment.id) ? ' gold' : ''}`}
                      style={{ cursor: 'pointer', gap: 6 }}
                      title={segment.cidrs.join(', ')}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSegments.includes(segment.id)}
                        onChange={() => setSelectedSegments((prev) => toggle(prev, segment.id))}
                      />
                      {segment.name}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="field">
              <label>บทบาทที่อนุญาต</label>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {roleOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`chip${selectedRoles.includes(option.value) ? ' gold' : ''}`}
                    style={{ cursor: 'pointer', gap: 6 }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(option.value)}
                      onChange={() => setSelectedRoles((prev) => toggle(prev, option.value))}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
              <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>ไม่เลือกเลยหมายถึงทุกบทบาท</div>
            </div>

            <div className="field">
              <label htmlFor="zt-departments">แผนก (คั่นด้วยจุลภาค)</label>
              <input id="zt-departments" className="input" value={departments}
                placeholder="เช่น audit, finance"
                onChange={(e) => setDepartments(e.target.value)} />
              <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>เว้นว่างหมายถึงทุกแผนก</div>
            </div>

            <div className="field">
              <label>สถานะอุปกรณ์</label>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {postureOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`chip${selectedPostures.includes(option.value) ? ' gold' : ''}`}
                    style={{ cursor: 'pointer', gap: 6 }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPostures.includes(option.value)}
                      onChange={() => setSelectedPostures((prev) => toggle(prev, option.value))}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
              <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>ไม่เลือกเลยหมายถึงทุกสถานะ</div>
            </div>

            <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <div className="field" style={{ flex: 1, minWidth: 130 }}>
                <label htmlFor="zt-protocol">โปรโตคอล</label>
                <select id="zt-protocol" className="select" value={protocol}
                  onChange={(e) => setProtocol(e.target.value as ZTProtocol)}>
                  {protocolOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ flex: 1, minWidth: 130 }}>
                <label htmlFor="zt-ports">พอร์ต (L4)</label>
                <input id="zt-ports" className="input" value={ports}
                  placeholder="443 หรือ 8000-8080"
                  onChange={(e) => setPorts(e.target.value)} />
              </div>
            </div>
            <div className="dim" style={{ fontSize: 11, marginTop: -8 }}>เว้นว่างหมายถึงทุกพอร์ต</div>

            <div className="field">
              <label htmlFor="zt-cidrs">CIDR ต้นทาง</label>
              <input id="zt-cidrs" className="input" value={cidrs}
                placeholder="เช่น 203.0.113.0/24, 10.10.0.0/16"
                onChange={(e) => setCidrs(e.target.value)} />
              <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                ต้องระบุอย่างน้อยหนึ่งช่วง ใช้ 0.0.0.0/0 เมื่อต้องการทุกเครือข่ายอย่างชัดเจน
              </div>
            </div>

            <div className="field">
              <label>วันที่ใช้ได้</label>
              <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                {dayLabels.map((label, index) => (
                  <label
                    key={label}
                    className={`chip${days.includes(index) ? ' gold' : ''}`}
                    style={{ cursor: 'pointer', minWidth: 38, justifyContent: 'center', gap: 4 }}
                  >
                    <input
                      type="checkbox"
                      checked={days.includes(index)}
                      onChange={() => setDays((prev) => toggle(prev, index))}
                    />
                    {label}
                  </label>
                ))}
              </div>
              <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>ไม่เลือกเลยหมายถึงทุกวัน</div>
            </div>

            <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <div className="field" style={{ flex: 1, minWidth: 120 }}>
                <label htmlFor="zt-window-start">เริ่ม (เวลาไทย)</label>
                <input id="zt-window-start" className="input" type="time" value={windowStart}
                  onChange={(e) => setWindowStart(e.target.value)} />
              </div>
              <div className="field" style={{ flex: 1, minWidth: 120 }}>
                <label htmlFor="zt-window-end">สิ้นสุด (เวลาไทย)</label>
                <input id="zt-window-end" className="input" type="time" value={windowEnd}
                  onChange={(e) => setWindowEnd(e.target.value)} />
              </div>
            </div>
            <div className="dim" style={{ fontSize: 11, marginTop: -8 }}>
              เริ่มเท่ากับสิ้นสุดหมายถึงตลอดวัน · เวลาสิ้นสุดไม่รวมในรอบ
            </div>

            <label className="row" style={{ gap: 8, marginTop: 16, fontSize: 12.5 }}>
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              เปิดใช้นโยบายนี้ทันที
            </label>

            {formError ? <div className="form-error">{formError}</div> : null}
            {notice ? <div className="demo-note" style={{ color: 'var(--color-emerald-soft)' }}>{notice}</div> : null}

            <button className="btn btn-primary" style={{ marginTop: 16 }}
              disabled={!canSubmit} onClick={submitPolicy}>
              {create.isPending ? 'กำลังบันทึก…' : 'บันทึกนโยบาย'}
            </button>
          </div>

          <div className="card card-pad">
            <div className="card-label">ประเมินการเข้าถึง</div>
            <div className="dim" style={{ fontSize: 12, lineHeight: 1.7, marginTop: 8 }}>
              การประเมินเป็นการตัดสินใจจริงและจะถูกบันทึกในบันทึกการตัดสินใจ
            </div>

            <div className="field">
              <label htmlFor="zt-eval-role">บทบาทที่ต้องการประเมิน</label>
              <select id="zt-eval-role" className="select" value={evalRole}
                onChange={(e) => setEvalRole(e.target.value as Role)} style={{ width: '100%' }}
                disabled={!canConfigure}>
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {!canConfigure ? (
                <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                  ประเมินได้เฉพาะบทบาทของตนเอง เฉพาะเจ้าของหรือผู้ดูแลระบบเท่านั้นที่เลือกบทบาทอื่นได้
                </div>
              ) : null}
            </div>

            <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <div className="field" style={{ flex: 1, minWidth: 130 }}>
                <label htmlFor="zt-eval-department">แผนก</label>
                <input id="zt-eval-department" className="input" value={evalDepartment}
                  placeholder="เช่น audit"
                  onChange={(e) => setEvalDepartment(e.target.value)} />
              </div>
              <div className="field" style={{ flex: 1, minWidth: 130 }}>
                <label htmlFor="zt-eval-posture">สถานะอุปกรณ์</label>
                <select id="zt-eval-posture" className="select" value={evalPosture}
                  onChange={(e) => setEvalPosture(e.target.value)} style={{ width: '100%' }}>
                  {postureOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field">
              <label htmlFor="zt-eval-source">IP ต้นทาง</label>
              <input id="zt-eval-source" className="input" value={evalSource}
                placeholder="เว้นว่างเพื่อใช้ IP ของคำขอนี้"
                onChange={(e) => setEvalSource(e.target.value)} />
            </div>

            <div className="field">
              <label htmlFor="zt-eval-segment">เซกเมนต์ปลายทาง</label>
              <select id="zt-eval-segment" className="select" value={chosenSegment}
                onChange={(e) => setEvalSegment(e.target.value)} style={{ width: '100%' }}>
                {segmentRows.length === 0 ? <option value="">ยังไม่มีเซกเมนต์</option> : null}
                {segmentRows.map((segment) => (
                  <option key={segment.id} value={segment.id}>{segment.name}</option>
                ))}
              </select>
            </div>

            <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <div className="field" style={{ flex: 1, minWidth: 110 }}>
                <label htmlFor="zt-eval-protocol">โปรโตคอล</label>
                <select id="zt-eval-protocol" className="select" value={evalProtocol}
                  onChange={(e) => setEvalProtocol(e.target.value as ZTProtocol)} style={{ width: '100%' }}>
                  {protocolOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ width: 100 }}>
                <label htmlFor="zt-eval-port">พอร์ต</label>
                <input id="zt-eval-port" className="input" inputMode="numeric" value={evalPort}
                  onChange={(e) => setEvalPort(e.target.value.replace(/[^0-9]/g, ''))} />
              </div>
            </div>

            <div className="field">
              <label htmlFor="zt-eval-destination">IP ปลายทาง (ไม่บังคับ)</label>
              <input id="zt-eval-destination" className="input" value={evalDestination}
                placeholder="เช่น 10.20.5.9 ต้องอยู่ใน CIDR ของเซกเมนต์"
                onChange={(e) => setEvalDestination(e.target.value)} />
            </div>

            {evaluate.isError ? (
              <div className="form-error">{errorText(evaluate.error, 'ประเมินการเข้าถึงไม่สำเร็จ')}</div>
            ) : null}

            <button className="btn btn-primary" style={{ marginTop: 16 }}
              disabled={evaluate.isPending || chosenSegment === ''} onClick={submitEvaluation}>
              {evaluate.isPending ? 'กำลังประเมิน…' : 'ประเมินการเข้าถึง'}
            </button>

            {decision ? (
              <div style={{
                marginTop: 16,
                border: '1px solid var(--color-hairline)',
                borderRadius: 14,
                padding: 14,
                background: decision.allowed ? 'rgba(46,125,100,0.06)' : 'rgba(199,123,43,0.06)',
              }}>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <span className="chip" style={{
                    color: decision.allowed ? 'var(--color-emerald-soft)' : 'var(--color-amber)',
                    borderColor: decision.allowed ? 'rgba(46,125,100,0.32)' : 'rgba(199,123,43,0.32)',
                  }}>
                    {decision.allowed ? 'อนุญาต' : 'ปฏิเสธ'}
                  </span>
                  {decision.matched_priority !== undefined ? (
                    <span className="chip">ลำดับ {decision.matched_priority}</span>
                  ) : (
                    <span className="chip">ค่าเริ่มต้นปฏิเสธ</span>
                  )}
                </div>
                <div style={{ fontSize: 13, marginTop: 10, lineHeight: 1.7 }}>{decision.reason}</div>
                <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.8 }}>
                  {decision.segment_name ? `เซกเมนต์ ${decision.segment_name}` : 'ไม่พบเซกเมนต์'}
                  {' · '}{decision.protocol.toUpperCase()} {decision.port}
                  {' · '}{decision.source_ip}
                  {decision.destination_ip ? ` · ปลายทาง ${decision.destination_ip}` : ''}
                  {decision.matched_policy_name ? ` · กฎ ${decision.matched_policy_name}` : ''}
                </div>
                <div className="mono muted" style={{ fontSize: 10.5, marginTop: 8 }}>{decision.decision_id ?? ''}</div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}
